import React from 'react';
import { Box, Typography, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import {
  PASSWORD_POLICY_RULES,
  type PasswordPolicyRule,
} from '../utilities/passwordPolicy';
import type { ExtraPasswordRule } from '../types/passwordRequirements';

type PasswordRequirementsProps = {
  password: string;
  title?: string;
  extraRules?: ExtraPasswordRule[];
  sx?: object;
};

function ruleMet(rule: PasswordPolicyRule, password: string): boolean {
  return rule.test(password);
}

const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({
  password,
  title = 'Password requirements:',
  extraRules = [],
  sx = {},
}) => {
  const started = password.length > 0;

  if (!started) {
    return (
      <Box sx={{ width: '100%', ...sx }}>
        <Typography className='ms-body2' color='textSecondary' component='div'>
          {title}
        </Typography>
        <Box
          component='ul'
          className='ms-body2'
          sx={{
            m: 0,
            mt: 1,
            pl: 2.5,
            color: theme => theme.palette.text.secondary,
          }}
        >
          {PASSWORD_POLICY_RULES.map(rule => (
            <Box component='li' key={rule.id} sx={{ mb: 0.25 }}>
              {rule.label}
            </Box>
          ))}
          {extraRules.map((er, i) => (
            <Box component='li' key={`extra-${i}`} sx={{ mb: 0.25 }}>
              {er.label}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  const rows: { key: string; label: string; isMet: boolean }[] = [
    ...PASSWORD_POLICY_RULES.map(rule => ({
      key: rule.id,
      label: rule.label,
      isMet: ruleMet(rule, password),
    })),
    ...extraRules.map((er, i) => ({
      key: `extra-${i}`,
      label: er.label,
      isMet: er.isSatisfied(password),
    })),
  ];

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <Typography className='ms-body2' color='textSecondary' component='div'>
        {title}
      </Typography>
      <List dense disablePadding sx={{ mt: 0.5 }}>
        {rows.map(row => (
          <ListItem key={row.key} disableGutters sx={{ py: 0.25, alignItems: 'flex-start' }}>
            <ListItemIcon sx={{ minWidth: 32, mt: 0.125 }}>
              {row.isMet ? (
                <CheckCircle sx={{ fontSize: 20 }} color='success' />
              ) : (
                <RadioButtonUnchecked sx={{ fontSize: 20 }} color='error' />
              )}
            </ListItemIcon>
            <ListItemText
              primary={row.label}
              slotProps={{
                primary: {
                  className: 'ms-body2',
                  sx: {
                    color: row.isMet ? 'success.main' : 'error.main',
                  },
                },
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default PasswordRequirements;
